import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "./Card";
import "./login.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Dialog from "@mui/material/Dialog";
import {
  loginPostData,
  handleForgotpassword,
  handleVerifyOTP,
  handleResendOTP,
  handleResetPassword,
} from "../../services/apiService";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

function LoginComponent() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const [forgotStep, setForgotStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isResendDisabled, setIsResendDisabled] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showBlast, setShowBlast] = useState(false);

  const [formData, setFormData] = useState({
    loginname: "",
    code: "",
    password: "",
  });

  const [error, setError] = useState(null);
  const [step, setStep] = useState(0);
  const [isForgotOpen, setIsForgotOpen] = useState(false);

  const tutorialData = [
    {
      title: "Learns from user inputs",
      description: "",
      bgColor: "time",
      image: "./login-one.jpg",
    },
    {
      title: "Finds solutions independently",
      description: "",
      bgColor: "programming",
      image: "./login-two.jpg",
    },
    {
      title: "Executes solutions for business goals",
      description: "",
      bgColor: "meditation",
      image: "./admin.png",
    },
  ];

  const stepFuncs = {
    nextStep: () => setStep(step + 1),
    prevStep: () => setStep(step - 1),
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleLogin = async () => {
    localStorage.setItem("loginName", formData.loginname);
    localStorage.setItem("clientCode", formData.code);

    try {
      const response = await loginPostData(formData);

      if (response?.success) {
        const storedRole =
          Array.isArray(response.details) && response.details.length > 0
            ? response.details[0]?.role
            : "User";

        toast.success(`You're verified! Role: ${storedRole}`, {
          autoClose: 2000,
        });
        navigate("/GYAANi");
      } else {
        const invalidCredMsg = /invalid credentials/i.test(response.message)
          ? "Invalid credentials"
          : response.message || "Login failed. Please try again.";

        toast.error(invalidCredMsg, { autoClose: 2000 });
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Login failed due to an unexpected error.";

      toast.error(errorMessage, { autoClose: 2000 });
      console.error("Login error:", err);
    }
  };

  const handleForgotOpen = () => setIsForgotOpen(true);
  const handleForgotClose = () => {
    setIsForgotOpen(false);
    setForgotStep(1);
    setEmail("");
    setOtp("");
    setNewPassword("");
    setConfirmPassword("");
    setIsResendDisabled(false);
  };

  const validateEmail = (value) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(value);
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setEmail(value);

    if (!validateEmail(value)) {
      setError("Please enter a valid email address");
    } else {
      setError("");
    }
  };

  const handleSendOTP = async () => {
    try {
      const requestedData = { email };
      const response = await handleForgotpassword(requestedData);

      if (response.success) {
        setForgotStep(2);
        toast.success("OTP sent successfully!", { autoClose: 2000 });
      } else {
        toast.error(response.message || "User not found", { autoClose: 2000 });
      }
    } catch (error) {
      console.error(error.message);
      toast.error("Network error: " + error.message, { autoClose: 2000 });
    }
  };

  const [isVerifying, setIsVerifying] = useState(false);

  const OTP_ERROR_TOAST_ID = "otp-error";

  const handleVerifyOTPDetails = async () => {
    if (!otp || isVerifying) return;

    setIsVerifying(true);
    try {
      const response = await handleVerifyOTP({ email, otp });

      if (response.success) {
        toast.success("OTP verified successfully!", { autoClose: 2000 });
        setForgotStep(3);
      } else if (!toast.isActive(OTP_ERROR_TOAST_ID)) {
        toast.error(response.message || "Invalid OTP", {
          toastId: OTP_ERROR_TOAST_ID,
          autoClose: 2000,
        });
      }
    } catch (error) {
      if (!toast.isActive(OTP_ERROR_TOAST_ID)) {
        toast.error("Network error: " + error.message, {
          toastId: OTP_ERROR_TOAST_ID,
          autoClose: 2000,
        });
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const RESEND_OTP_TOAST_ID = "resend-otp-error";

  const handleResendOTPDetails = async () => {
    try {
      const requestedResendOTPData = { email };
      console.log(email);

      const response = await handleResendOTP(requestedResendOTPData);

      if (response.success) {
        toast.success("OTP sent successfully!", { autoClose: 2000 });
      } else {
        toast.error(response.message || "User not found", {
          toastId: RESEND_OTP_TOAST_ID, // ✅ ensures only one toast exists
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error(error.message);
      toast.error("Network error: " + error.message, {
        toastId: RESEND_OTP_TOAST_ID,
        autoClose: 2000,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match", { autoClose: 2000 });
      return;
    }

    try {
      const data = { newPassword, confirmPassword, email }; // include email or user ID as needed
      const response = await handleResetPassword(data);

      if (response.success) {
        toast.success("Password changed successfully!", { autoClose: 2000 });

        // Trigger the blast animation
        setShowBlast(true);

        // Close modal after animation
        setTimeout(() => {
          setShowBlast(false);
          handleForgotClose();
        }, 2000);
      } else {
        toast.error(response.message || "Password reset failed", {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error(error);
      toast.error("Network error: " + error.message, { autoClose: 2000 });
    }
  };

  return (
    <>
      <div className="flex justify-center items-center min-h-screen authentication-background">
        <div className="w-full max-w-6xl border rounded-lg p-10 bg-white relative shadow-lg">
          <div className="flex flex-col md:flex-row">
            <div className="md:w-2/3 flex flex-col justify-center items-center p-6 rounded-lg">
              <Card
                currentCardData={tutorialData[step]}
                stepFuncs={stepFuncs}
                step={step}
                tutorialData={tutorialData}
                setStep={setStep}
              >
                {tutorialData.map((data) => (
                  <img
                    src={data.image}
                    className={`bg-${tutorialData[step].bgColor} duration-500 h-min`}
                  />
                ))}
                {tutorialData.map((data) => (
                  <h6 className="font-bold text-center text-xl pt-5 px-3 h-1/3 min-w-full">
                    {data.title}
                  </h6>
                ))}
                {tutorialData.map((data) => (
                  <p className="font-thin text-sm py-3 ps-3 pe-10 min-w-full">
                    {data.description}
                  </p>
                ))}
              </Card>

              <small className="absolute bottom-0 text-black text-center font-mono max-w-xs mb-2">
                GenAI Powered Data-Driven Automation
              </small>
            </div>

            <div className="md:w-1/2 p-10">
              <p className="text-black text-4xl font-semibold font-mono mb-3">
                Be Verified
              </p>
              <p className="text-gray-600">We are happy to have you back...</p>

              <div className="mb-4 pb-6">
                <input
                  type="text"
                  name="loginname"
                  value={formData.loginname}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Login Name"
                />
              </div>

              <div className="mb-4 pb-6">
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Client Code"
                />
              </div>

              <div className="mb-4 pb-6">
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Password"
                />
              </div>

              <div>
                <button
                  className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition duration-200"
                  onClick={handleLogin}
                >
                  Login
                </button>
                {error && <p className="text-red-500 mt-4">{error}</p>}
              </div>

              <div className="mt-4">
                <h5
                  className="text-blue-500 text-sm cursor-pointer hover:underline text-end"
                  onClick={handleForgotOpen}
                >
                  Forgot Password?
                </h5>
              </div>
            </div>

            {/* Forgot Password Dialog */}

            <Dialog
              open={isForgotOpen}
              onClose={handleForgotClose}
              aria-labelledby="forgot-password-dialog"
              PaperProps={{
                sx: {
                  width: "80%",
                  maxWidth: "1200px", // Set your desired max width
                },
              }}
            >
              <div
                className="flex flex-col md:flex-row items-center justify-center min-h-[80vh] p-12 gap-20"
                style={{
                  background:
                    "linear-gradient(45deg, rgb(251 235 235), transparent)",
                }}
              >
                <div className="w-full md:w-2/5 text-center bg-white px-10 py-12 rounded-xl shadow-lg">
                  {/* Progress Bar */}
                  <div className="flex items-center justify-between mb-8">
                    <div className="flex-1 h-2 bg-gray-300 rounded overflow-hidden">
                      <div
                        className={`h-full bg-blue-400 transition-all duration-500`}
                        style={{ width: `${(forgotStep / 3) * 100}%` }}
                      ></div>
                    </div>
                  </div>

                  {forgotStep === 1 && (
                    <>
                      <h1 className="text-2xl font-bold mb-4">
                        Forgot Password
                      </h1>
                      <p className="text-slate-500 mb-4">
                        Enter your email to receive a 6-digit OTP code.
                      </p>

                      <form
                        onSubmit={async (e) => {
                          e.preventDefault();
                          await handleSendOTP();
                        }}
                      >
                        <input
                          type="email"
                          className="w-full p-3 mb-4 border rounded-lg"
                          placeholder="Enter your email"
                          value={email}
                          onChange={handleChange}
                          required
                        />

                        <button
                          type="submit"
                          className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition duration-200"
                        >
                          Send OTP
                        </button>
                      </form>
                    </>
                  )}

                  {forgotStep === 2 && (
                    <>
                      <h1 className="text-2xl font-bold mb-4">
                        OTP Verification
                      </h1>
                      <p className="text-slate-500 mb-4">
                        Enter the OTP sent to your email:{" "}
                        <span className="font-semibold text-blue-600">
                          {email}
                        </span>
                      </p>

                      <form onSubmit={(e) => e.preventDefault()}>
                        <input
                          type="text"
                          className="w-full p-3 mb-4 border rounded-lg"
                          placeholder="Enter 6-digit OTP"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value)}
                          maxLength={6}
                          required
                        />

                        <button
                          type="button"
                          className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition duration-200"
                          onClick={() => {
                            if (!otp) {
                              toast.error("Please enter OTP", {
                                autoClose: 2000,
                              });
                              return;
                            }
                            handleVerifyOTPDetails(); // verify OTP in background
                          }}
                        >
                          Verify OTP
                        </button>
                      </form>

                      <div className="flex justify-between mt-4">
                        <button
                          className={`text-sm text-indigo-500 ${
                            isResendDisabled
                              ? "cursor-not-allowed opacity-50"
                              : "hover:underline"
                          }`}
                          disabled={isResendDisabled}
                          onClick={() => {
                            handleResendOTPDetails();
                            setIsResendDisabled(true);
                            setTimeout(() => setIsResendDisabled(false), 30000);
                          }}
                        >
                          Resend OTP
                        </button>

                        <button
                          className="text-sm text-gray-500 hover:underline"
                          onClick={() => setForgotStep(1)}
                        >
                          Go Back
                        </button>
                      </div>
                    </>
                  )}

                  {forgotStep === 3 && (
                    <>
                      <h1 className="text-2xl font-bold mb-4">
                        Set New Password
                      </h1>

                      <form onSubmit={handleSubmit}>
                        {/* New Password */}
                        <div className="relative mb-4">
                          <input
                            type={showNewPassword ? "text" : "password"}
                            className="w-full p-3 pr-12 border rounded-lg outline-none"
                            placeholder="New Password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className="absolute inset-y-0 right-3 flex items-center focus:outline-none"
                          >
                            {showNewPassword ? (
                              <VisibilityOff className="text-gray-600" />
                            ) : (
                              <Visibility className="text-gray-600" />
                            )}
                          </button>
                        </div>

                        {/* Confirm Password */}
                        <div className="relative mb-4">
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            className="w-full p-3 pr-12 border rounded-lg outline-none"
                            placeholder="Confirm New Password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            className="absolute inset-y-0 right-3 flex items-center focus:outline-none"
                          >
                            {showConfirmPassword ? (
                              <VisibilityOff className="text-gray-600" />
                            ) : (
                              <Visibility className="text-gray-600" />
                            )}
                          </button>
                        </div>

                        {/* Submit Button */}
                        <div className="relative">
                          <button
                            type="submit"
                            className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition duration-200"
                          >
                            Change Password
                          </button>

                          {/* Blast Animation */}
                          {showBlast && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <svg
                                className="w-24 h-24 animate-blast"
                                viewBox="0 0 100 100"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <circle
                                  cx="50"
                                  cy="50"
                                  r="0"
                                  stroke="#FFD700"
                                  strokeWidth="4"
                                >
                                  <animate
                                    attributeName="r"
                                    from="0"
                                    to="40"
                                    dur="0.5s"
                                    fill="freeze"
                                  />
                                  <animate
                                    attributeName="opacity"
                                    from="1"
                                    to="0"
                                    dur="1.5s"
                                    fill="freeze"
                                  />
                                </circle>
                              </svg>
                            </div>
                          )}
                        </div>
                      </form>
                    </>
                  )}

                  <div className="flex justify-end mt-4">
                    <CloseIcon
                      className="cursor-pointer text-black hover:text-red-500"
                      onClick={handleForgotClose}
                      fontSize="medium"
                    />
                  </div>
                </div>

                <div className="hidden md:block w-2/5">
                  <img src="./illustration.svg" alt="" />
                </div>
              </div>
            </Dialog>
          </div>
        </div>
      </div>

      <ToastContainer autoClose={2000} />
    </>
  );
}

export default LoginComponent;
